import React, { useEffect, useRef, useState } from 'react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import MessageAttachments from 'features/Chat/popup/MessageAttachments'
import MessageActions from 'features/Chat/shared/MessageActions'
import MessageReactions, { myReactionOf } from 'features/Chat/shared/MessageReactions'
import StyledChatMessage from './ChatMessage.style'
import type { IReaction } from 'features/Chat/Chat.types'

export type TAttachment = {
	url: string
	name?: string
	kind?: 'image' | 'video' | 'audio' | 'file'
	mimeType?: string
}

export type TMessage = {
	body: string
	username?: string
	time?: string
	roomId?: string
	avatar?: string
	attachments?: TAttachment[]
}

export interface Props {
	className?: string
	style?: string
	message: TMessage
	isAlt?: Boolean
	avatar?: string
	/**
	 * Reaction and edit support. All optional, so the fixture-driven previews
	 * and any caller that only wants a rendered message keep working.
	 */
	messageId?: string
	userId?: string
	reactions?: IReaction[]
	editedAt?: string
	/** True only for the user's most recent message, matching the server rule. */
	isEditable?: boolean
	onEdit?: (messageId: string, text: string) => Promise<void>
	onReact?: (messageId: string, emoji: string) => void
}

const ChatMessage = ({
	className = '',
	style,
	message,
	isAlt = false,
	avatar,
	messageId,
	userId,
	reactions = [],
	editedAt,
	isEditable = false,
	onEdit,
	onReact,
}: Props) => {
	const { body, time, username, attachments = [] } = message
	const showAvatar = username || avatar
	const showMeta = username || time

	const [hovered, setHovered] = useState(false)
	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState(body || '')
	const [isSaving, setIsSaving] = useState(false)
	const editRef = useRef<HTMLTextAreaElement>(null)

	const canAct = !!messageId

	useEffect(() => {
		if (!isEditing) return
		setDraft(body || '')
		requestAnimationFrame(() => {
			const el = editRef.current
			if (!el) return
			el.focus()
			el.setSelectionRange(el.value.length, el.value.length)
		})
		// eslint-disable-next-line
	}, [isEditing])

	const save = async () => {
		const text = draft.trim()
		if (!messageId || !text || text === body) {
			setIsEditing(false)
			return
		}
		setIsSaving(true)
		try {
			await onEdit?.(messageId, text)
			setIsEditing(false)
		} catch {
			// The server refused, most likely because a newer message landed.
			// Keep the editor open with the text rather than discarding it.
		} finally {
			setIsSaving(false)
		}
	}

	const onEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			setIsEditing(false)
			return
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			save()
		}
	}

	return (
		<StyledChatMessage
			data-testid="ChatMessage"
			data-message-id={messageId}
			className={className}
			isAlt={isAlt}
			style={style}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<div className="ChatMessage__avatar">
				{showAvatar && (
					<Avatar>
						<AvatarImage src={avatar} alt={username} />
						<AvatarFallback />
					</Avatar>
				)}
			</div>
			{/* While editing, the inner column drops its 75% cap and takes the rest
			    of the row, so the editor is as wide as the chat container rather
			    than as wide as the bubble happened to be. The `!` prefixes are
			    needed because the styled-component's rules are descendant
			    selectors and so outrank a plain utility class. */}
			<div className={cn('ChatMessage__inner', isEditing && '!max-w-full flex-1')}>
				{showMeta && (
					<div className="ChatMessage__meta">
						{username && <span className="ChatMessage__meta__username">{username}</span>}
						{time && <span>{time}</span>}
						{editedAt && !isEditing && <span className="ml-1 italic">(edited)</span>}
					</div>
				)}

				<div className={cn('flex items-end gap-1.5', isEditing && 'w-full')}>
					{isEditing ? (
						<div className="ChatMessage__body flex !w-full flex-col gap-1">
							<textarea
								ref={editRef}
								value={draft}
								disabled={isSaving}
								onChange={e => setDraft(e.target.value)}
								onKeyDown={onEditKeyDown}
								rows={Math.min(8, (draft.match(/\n/g)?.length ?? 0) + 1)}
								aria-label="Edit message"
								className="w-full resize-none bg-transparent text-[16px] leading-snug outline-none"
							/>
							<span className="text-[10px] text-gray-500">Enter to save · Esc to cancel</span>
						</div>
					) : (
						body && <div className="ChatMessage__body">{body}</div>
					)}

					{canAct && !isEditing && (
						<MessageActions
							messageId={messageId as string}
							align="left"
							isVisible={hovered}
							canEdit={isEditable && !!onEdit}
							myReaction={myReactionOf(reactions, userId)}
							onReact={onReact}
							onStartEdit={() => setIsEditing(true)}
						/>
					)}
				</div>

				<MessageAttachments attachments={attachments} />

				{reactions.length > 0 && (
					<MessageReactions
						className="mt-1"
						reactions={reactions}
						userId={userId}
						onToggle={emoji => messageId && onReact?.(messageId, emoji)}
					/>
				)}
			</div>
		</StyledChatMessage>
	)
}

export default ChatMessage
